import moment from 'moment';

import { legTemplate as legTemplateStructure } from '@/dataStructures';
import * as validators from '@/modules/newTrip/validation';
import { createDateObject, } from '@/core';

function addLeg({ state }) {
	let legNumber = state.templateDetails.legs.length;
	let leg = {
		...legTemplateStructure(),
		tripTemplateId: state.templateDetails.id,
		legNumber,
		status: state.templateDetails.status,
		duration: undefined, // eta - etd days
		departureTime: undefined, // etd time
		arrivalTime: undefined,	// eta time
	};
	state.templateDetails.legs.push(leg);
}

function removeLeg({ state }, legNumber) {
	if (typeof legNumber !== 'number') throw new Error(`legNumber (${legNumber}) is not a number`);
	state.dirty = true;
	state.templateDetails.legs.splice(legNumber, 1);
	for (let i = legNumber; i < state.templateDetails.legs.length; i++) {
		state.templateDetails.legs[i].legNumber = i;
	}
}

function swapLegOrder({ state }, { legNumberBefore, legNumberAfter }) {
	if (typeof (legNumberBefore) === 'undefined' || typeof (legNumberAfter) === 'undefined') {
		throw new Error(`swapLegOrder called with invalid arguments: ${legNumberBefore} -> ${legNumberAfter}`);
	}
	if (legNumberBefore < 0 || legNumberBefore >= state.templateDetails.legs.length) throw new Error(`legNumberBefore (${legNumberBefore}) out of bounds`);
	if (legNumberBefore < 0 || legNumberAfter >= state.templateDetails.legs.length) throw new Error(`legNumberAfter (${legNumberAfter}) out of bounds`);
	state.dirty = true;

	let leg1 = state.templateDetails.legs.find(x => x.legNumber === legNumberBefore);
	let leg2 = state.templateDetails.legs.find(x => x.legNumber === legNumberAfter);
	leg1.legNumber = legNumberAfter;
	leg2.legNumber = legNumberBefore;
	state.templateDetails.legs.sort((a, b) => a.legNumber - b.legNumber);
}

function setLegStatus({ commit }, { legNumber, status }) {
	commit('setDetails', { path: `legs.${legNumber}.status`, value: status });
}

function setLegFromAddress({ commit, dispatch }, { legNumber, ...value }) {
	commit('setDetails', { path: `legs.${legNumber}.fromAddress`, value: value });
	commit('setDetails', { path: `legs.${legNumber}.fromAddressId`, value: value.id });
	dispatch('validateLegFromAndToAddresses', legNumber);
}

function setLegToAddress({ commit, dispatch }, { legNumber, ...value }) {
	commit('setDetails', { path: `legs.${legNumber}.toAddress`, value: value });
	commit('setDetails', { path: `legs.${legNumber}.toAddressId`, value: value.id });
	dispatch('validateLegFromAndToAddresses', legNumber);
}

function setLegDepartureTime({ commit, dispatch }, { legNumber, departureTime }) {
	commit('setDetails', { path: `legs.${legNumber}.departureTime`, value: moment(departureTime, 'LT').format('HH:mm') });
	dispatch('validateLegTimes', legNumber);
}

function setLegArrivalTime({ commit, dispatch }, { legNumber, arrivalTime }) {
	commit('setDetails', { path: `legs.${legNumber}.arrivalTime`, value: moment(arrivalTime, 'LT').format('HH:mm') });
	dispatch('validateLegTimes', legNumber);
}

function setLegDuration({ commit, dispatch }, { legNumber, duration }) {
	commit('setDetails', { path: `legs.${legNumber}.duration`, value: duration });
	dispatch('validateLegTimes', legNumber);
}

function setLegCargoCarrier({ commit, dispatch }, { legNumber, ...cargoCarrier }) {
	if (!cargoCarrier || !Object.keys(cargoCarrier).length) {
		commit('setDetails', { path: `legs.${legNumber}.cargoCarrier`, cargoCarrier: undefined });
		commit('setDetails', { path: `legs.${legNumber}.cargoCarrierId`, cargoCarrier: undefined });
		return;
	}

	commit('setDetails', { path: `legs.${legNumber}.cargoCarrier`, value: cargoCarrier });
	commit('setDetails', { path: `legs.${legNumber}.cargoCarrierId`, value: cargoCarrier.id });
	if (cargoCarrier.vendor) dispatch('setLegVendor', { legNumber, ...cargoCarrier.vendor });
	if (cargoCarrier.driver) dispatch('setLegDriver', { legNumber, fromCargoCarrier: true, ...cargoCarrier.driver });
}

function setLegVendor({ commit }, { legNumber, ...value }) {
	let vendor = value || {};
	commit('setDetails', { path: `legs.${legNumber}.vendor`, value: vendor });
	commit('setDetails', { path: `legs.${legNumber}.vendorId`, value: vendor.id });
	// TODO: Validate leg vendor?
}

function setLegNeedAccept({ commit }, { legNumber, needAccept }) {
	commit('setDetails', { path: `legs.${legNumber}.needAccept`, value: needAccept });

}

function setLegDriver({ state, commit }, { legNumber, fromCargoCarrier, ...value }) {
	if (fromCargoCarrier) {
		if (state.templateDetails.legs[legNumber].driverIsSetManually) return;
	} else {
		commit('setDetails', { path: `legs.${legNumber}.driverIsSetManually`, value: true });
	}
	let driver = value || {};
	commit('setDetails', { path: `legs.${legNumber}.driver`, value: { ...driver } });
	commit('setDetails', { path: `legs.${legNumber}.driverId`, value: driver.id });
}

/* Leg validation */
const createValidatorAction = validator => (...params) => {
	let [{ state }] = params;
	if (!state.errors.isInvalid) return; // Only start validating single fields once errors are shown.
	if (state.templateDetails.status < 10) return; // Trip status is "draft" or "cancelled", so anything goes.
	validator(...params);
};

const hydrateWithEtdAndEta = templateDetails => ({
	...templateDetails,
	etd: templateDetails.departureTime
			&& (typeof templateDetails.departureTime === 'string')
			&& templateDetails.departureTime.includes(':')
		? createDateObject( { date: moment(), hasTime: true, time: templateDetails.departureTime })
		: null,
	eta: templateDetails.arrivalTime
			&& (typeof templateDetails.arrivalTime === 'string')
			&& templateDetails.arrivalTime.includes(':')
		? createDateObject( { date: moment().add(templateDetails.duration, 'd'), hasTime: true, time: templateDetails.arrivalTime })
		: null,
});

const validateLegTimes = createValidatorAction(({ state, commit }, legNumber) => {
	let errors = validators.etdAndEta(hydrateWithEtdAndEta(state.templateDetails.legs[legNumber]));
	console.log({ validateLegTimes: errors });
	commit('setErrors', { path: `legs.${legNumber}.etd`, value: errors.etd });
	commit('setErrors', { path: `legs.${legNumber}.eta`, value: errors.eta });
});

const validateLegFromAndToAddresses = createValidatorAction(({ state, commit }, legNumber) => {
	let allowEqualAddresses = true; // We don't want leg-validation to be this complicated.
	let errors = validators.fromAndToAddresses(state.templateDetails.legs[legNumber], allowEqualAddresses);
	commit('setErrors', { path: `legs.${legNumber}.fromAddress`, value: errors.fromAddress });
	commit('setErrors', { path: `legs.${legNumber}.toAddress`, value: errors.toAddress });
});

/* -------------- */

export default {
	addLeg,
	removeLeg,
	swapLegOrder,
	setLegStatus,
	setLegFromAddress,
	setLegToAddress,
	setLegDepartureTime,
	setLegArrivalTime,
	setLegDuration,
	setLegCargoCarrier,
	setLegVendor,
	setLegNeedAccept,
	setLegDriver,

	validateLegTimes,
	validateLegFromAndToAddresses,
};
